import { db } from './micuDatabase';
import { getStoredPAT } from './micuAuth';

const MAX_RETRIES = 5;
const BASE_DELAY = 1000;

let syncInProgress = false;
let retryCount = 0;

function getBackoffDelay() {
  return Math.min(BASE_DELAY * Math.pow(2, retryCount), 30000);
}

async function getAuthHeaders() {
  const pat = await getStoredPAT();
  if (!pat) return null;
  return {
    'Content-Type': 'application/json',
    'X-MICU-Token': pat,
  };
}

async function processSyncQueue() {
  if (syncInProgress || !navigator.onLine) return;
  syncInProgress = true;

  try {
    const items = await db.syncQueue.orderBy('createdAt').toArray();
    if (items.length === 0) {
      syncInProgress = false;
      retryCount = 0;
      return;
    }

    const headers = await getAuthHeaders();
    if (!headers) {
      syncInProgress = false;
      return;
    }

    // Batch sync: send all items at once
    const response = await fetch('/micu/sync', {
      method: 'POST',
      headers,
      body: JSON.stringify({ changes: items }),
    });

    if (response.ok) {
      const result = await response.json();

      // Clear synced items
      const syncedIds = items.map(i => i.id);
      await db.syncQueue.bulkDelete(syncedIds);

      // Update sync status on local records
      for (const item of items) {
        if (item.action !== 'delete') {
          try {
            await db[item.table].update(item.recordId, { syncStatus: 'synced' });
          } catch { /* record may have been deleted locally */ }
        }
      }

      // Update remoteIds if server returned them
      if (result.remoteIds) {
        for (const [table, mappings] of Object.entries(result.remoteIds)) {
          for (const [localId, remoteId] of Object.entries(mappings)) {
            try {
              await db[table].update(Number(localId), { remoteId });
            } catch { /* noop */ }
          }
        }
      }

      retryCount = 0;
    } else if (response.status === 409) {
      // Conflict — last-write-wins: clear the queue items and refetch
      const syncedIds = items.map(i => i.id);
      await db.syncQueue.bulkDelete(syncedIds);
      retryCount = 0;
    } else if (response.status === 501) {
      // Backend not configured yet — stop retrying, keep queue for later
      retryCount = MAX_RETRIES;
    } else {
      retryCount = Math.min(retryCount + 1, MAX_RETRIES);
    }
  } catch (err) {
    retryCount = Math.min(retryCount + 1, MAX_RETRIES);
  } finally {
    syncInProgress = false;
  }

  // If there are remaining items, schedule a retry
  const remaining = await db.syncQueue.count();
  if (remaining > 0 && retryCount < MAX_RETRIES) {
    setTimeout(processSyncQueue, getBackoffDelay());
  }
}

// Start sync engine: listen for online events and periodically sync
export function startSyncEngine() {
  window.addEventListener('online', () => {
    retryCount = 0;
    processSyncQueue();
  });

  // Periodic sync every 60 seconds when online
  setInterval(() => {
    if (navigator.onLine) {
      processSyncQueue();
    }
  }, 60000);

  // Initial sync
  if (navigator.onLine) {
    processSyncQueue();
  }
}

// Force a manual sync
export function triggerSync() {
  retryCount = 0;
  return processSyncQueue();
}

// Get pending sync count
export async function getPendingSyncCount() {
  return db.syncQueue.count();
}
